import {
  UserAccount,
  UserContactFieldPreference,
} from "@/models/user-accounts";
import { defineStore } from "pinia";
import { computed, ComputedRef, ref } from "vue";
import { userAccountsService } from "@/services/user-accounts.service";

type SMSPartsLimitType = {
  hard: number;
  soft: number;
};
export interface UserAccountStateModule {
  contactFieldsPreference?: UserContactFieldPreference;
  defaultPreferenceState: string;
  secondaryPreferenceState: string;
  userProfile: UserAccount;
  showTimeZoneInfo: boolean;
  defaultContactFolderId: string;
  showFullTimeText: boolean;
  SMSPartsLimit: SMSPartsLimitType;
  enable24HrFormat: boolean;
  dateTimeFormat: string;
}

export const useUserAccountsStore = defineStore("userAccounts", () => {
  // STATES
  const contactFieldsPreference = ref<UserContactFieldPreference>();
  const defaultPreferenceState = ref<string>("mobile");
  const secondaryPreferenceState = ref<string>("email");
  const userProfile = ref<UserAccount>(new UserAccount());
  const showTimeZoneInfo = ref<boolean>(true);
  const SMSPartsLimit = ref<SMSPartsLimitType>({
    hard: 0,
    soft: 0,
  });
  const defaultContactFolderId = ref<string>("");
  const showFullTimeText = ref<boolean>(false);
  const enable24HrFormat = ref<boolean>(true);
  const dateTimeFormat = ref<string>("MMM D, YYYY");

  // ACTIONS
  const getContactSettings = async () => {
    const { data } = await userAccountsService.getUserProfile();
    if (data.primaryAddress !== null && data.secondaryAddress !== null) {
      defaultPreferenceState.value = data.primaryAddress;
      secondaryPreferenceState.value = data.secondaryAddress;
      defaultContactFolderId.value = data.defaultContactFolderId;
    } else {
      defaultPreferenceState.value = "mobile";
      secondaryPreferenceState.value = "email";
    }
  };

  const setPrimaryAddress = async (payload: {
    primaryAddress: string;
    secondaryAddress: string;
    defaultContactFolderId?: string;
  }) => {
    await userAccountsService.setPrimaryAddress(payload);
    defaultPreferenceState.value = payload.primaryAddress;
    secondaryPreferenceState.value = payload.secondaryAddress;
    defaultContactFolderId.value = payload?.defaultContactFolderId ?? "";
  };

  const setSettingsUserProfile = async () => {
    const { data } = await userAccountsService.getUserAccountUserProfile();
    userProfile.value = data || {};
    showTimeZoneInfo.value = data.enableTimezone;
    showFullTimeText.value = data.showTimeText;
    enable24HrFormat.value = data.enable24HrFormat ?? enable24HrFormat.value;
    dateTimeFormat.value = data.dateTimeFormat ?? dateTimeFormat.value;
    SMSPartsLimit.value = {
      soft: data.smsPartsSoftLimit,
      hard: data.smsPartsHardLimit,
    };
  };

  const setTimeZoneInfoFlag = (flag: boolean) => {
    showTimeZoneInfo.value = flag;
  };

  const setFullTimeTextFlag = (flag: boolean) => {
    showFullTimeText.value = flag;
  };

  const setDefaultTimeFormat = (format: boolean) => {
    enable24HrFormat.value = format;
  };

  const setDateFormat = (format: string) => {
    dateTimeFormat.value = format;
  };

  // GETTERS
  const getContactFieldsPreference: ComputedRef<
    UserAccountStateModule["contactFieldsPreference"]
  > = computed(() => contactFieldsPreference.value);
  const getUserProfile: ComputedRef<UserAccountStateModule["userProfile"]> =
    computed(() => userProfile.value);
  const getDefaultContactFolderId: ComputedRef<
    UserAccountStateModule["defaultContactFolderId"]
  > = computed(() => defaultContactFolderId.value);
  const getShowTimeZoneInfo: ComputedRef<
    UserAccountStateModule["showTimeZoneInfo"]
  > = computed(() => showTimeZoneInfo.value);
  const getShowFullTimeText: ComputedRef<
    UserAccountStateModule["showFullTimeText"]
  > = computed(() => showFullTimeText.value);
  const getSMSPartsLimitType: ComputedRef<
    UserAccountStateModule["SMSPartsLimit"]
  > = computed(() => SMSPartsLimit.value);
  const getDefaultPrimaryAddress: ComputedRef<
    UserAccountStateModule["defaultPreferenceState"]
  > = computed(() => defaultPreferenceState.value);
  const getDefaultSecondaryAddress: ComputedRef<
    UserAccountStateModule["secondaryPreferenceState"]
  > = computed(() => secondaryPreferenceState.value);
  const enable24TimeFormat: ComputedRef<
    UserAccountStateModule["enable24HrFormat"]
  > = computed(() => enable24HrFormat.value);
  const getDateFormat: ComputedRef<UserAccountStateModule["dateTimeFormat"]> =
    computed(() => dateTimeFormat.value);

  return {
    getContactFieldsPreference,
    getUserProfile,
    getDefaultContactFolderId,
    getShowTimeZoneInfo,
    getShowFullTimeText,
    getSMSPartsLimitType,
    getDefaultPrimaryAddress,
    getDefaultSecondaryAddress,
    enable24TimeFormat,
    getDateFormat,
    getContactSettings,
    setPrimaryAddress,
    setSettingsUserProfile,
    setTimeZoneInfoFlag,
    setFullTimeTextFlag,
    setDefaultTimeFormat,
    setDateFormat,
  };
});
